import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { TravellerOverviewData } from '../types/travellerAccess';

interface GenerateTokenRequest {
  tripId: string;
  travellerId: string;
}

interface GenerateTokenResponse {
  token: string;
  message: string;
}

interface GetTravellerDataRequest {
  token: string;
}

interface ExistingTokenResponse {
  hasToken: boolean;
  token: string | null;
  message: string;
  createdAt?: string;
}

/**
 * Service for managing traveller access tokens and data
 */
export class TravellerAccessService {
  private static generateTokenFunction = httpsCallable<GenerateTokenRequest, GenerateTokenResponse>(
    functions,
    'generateTravellerAccessToken'
  );

  private static getExistingTokenFunction = httpsCallable<GenerateTokenRequest, ExistingTokenResponse>(
    functions,
    'getExistingTravellerAccessToken'
  );

  private static getTravellerDataFunction = httpsCallable<GetTravellerDataRequest, TravellerOverviewData>(
    functions,
    'getTravellerDataByToken'
  );

  private static deactivateTokenFunction = httpsCallable<{ token: string }, { message: string }>(
    functions,
    'deactivateTravellerAccessToken'
  );

  private static cleanupTokensFunction = httpsCallable<{ tripId: string }, { message: string; deletedCount: number }>(
    functions,
    'cleanupTravellerAccessTokens'
  );

  /**
   * Generates a new access token for a traveller (admin only)
   */
  static async generateAccessToken(tripId: string, travellerId: string): Promise<string> {
    try {
      const result = await this.generateTokenFunction({ tripId, travellerId });
      return result.data.token;
    } catch (error) {
      console.error('Error generating access token:', error);
      throw new Error('Failed to generate access token');
    }
  }

  /**
   * Checks if a traveller has an existing valid access token (admin only)
   */
  static async getExistingAccessToken(tripId: string, travellerId: string): Promise<{hasToken: boolean, token: string | null}> {
    try {
      const result = await this.getExistingTokenFunction({ tripId, travellerId });
      return { hasToken: result.data.hasToken, token: result.data.token };
    } catch (error) {
      console.error('Error checking existing access token:', error);
      throw new Error('Failed to check existing access token');
    }
  }

  /**
   * Retrieves traveller data using an access token (public access)
   */
  static async getTravellerData(token: string): Promise<TravellerOverviewData> {
    try {
      const result = await this.getTravellerDataFunction({ token });
      return result.data;
    } catch (error) {
      console.error('Error retrieving traveller data:', error);
      throw new Error('Failed to retrieve traveller data');
    }
  }

  /**
   * Deactivates an access token (admin only)
   */
  static async deactivateAccessToken(token: string): Promise<void> {
    try {
      await this.deactivateTokenFunction({ token });
    } catch (error) {
      console.error('Error deactivating access token:', error);
      throw new Error('Failed to deactivate access token');
    }
  }

  /**
   * Cleans up all access tokens for a trip (admin only, maintenance function)
   */
  static async cleanupAccessTokensForTrip(tripId: string): Promise<{ deletedCount: number }> {
    try {
      const result = await this.cleanupTokensFunction({ tripId });
      return { deletedCount: result.data.deletedCount };
    } catch (error) {
      console.error('Error cleaning up access tokens:', error);
      throw new Error('Failed to cleanup access tokens');
    }
  }

  /**
   * Generates a shareable URL for a traveller
   */
  static generateShareableUrl(token: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/traveller/${token}`;
  }
}